import groupBy from "lodash/groupBy";
import keyBy from "lodash/keyBy";
import get from "lodash/get";

import { createSelector } from "@reduxjs/toolkit";

import { GLOBAL_COUNTRY } from "@stadium/constants/country";
import { TAXABLE_NUMBER_COUNTRY } from "@stadium/helpers/country/constants";
import { RESTRICTION_TAXON_NAME } from "@stadium/constants/products";
import { DEFAULT_CURRENCY_CODE } from "@stadium/constants";
import { getLogoDpiConfigFromApi } from "@stadium/helpers/logo-dpi-config";

const emptyObj = {};
const emptyArr = [];

const DEFAULT_PLATFORM_FEE_LABEL = "Platform Fee";
const DEFAULT_PLATFORM_FEE = 15;
const DEFAULT_POMP_FEE = 0;

export const selectConfigSlice = (state: any) => state?.config;

export const getAllCountries = createSelector(
  selectConfigSlice,
  (configState) => configState?.countries,
);

// TODO: Remove all get selectors and use select one

export const getUsaCountryId = createSelector(
  selectConfigSlice,
  (configState) => configState?.usCountryId,
);

export const getIsConfigLoading = createSelector(
  selectConfigSlice,
  (configState) => configState?.isFetching,
);

export const getLdAnonymousSecureModeHash = createSelector(
  selectConfigSlice,
  (configState) => configState?.ldAnonymousSecureModeHash,
);

export const getCurrencies = createSelector(
  selectConfigSlice,
  (configState) => configState?.currencies || emptyArr,
);

export const getCurrenciesArray = (state) => get(state, "config.currencies", emptyArr);

export const getDefaultCurrencyObject = createSelector(getCurrenciesArray, (currencies) =>
  currencies.find(({ code }) => code === DEFAULT_CURRENCY_CODE),
);

export const selectedCurrencyCodeMapping = createSelector(getCurrencies, (currencies) =>
  keyBy(currencies, "code"),
);

export const getDefaultCurrencyCode = createSelector(
  selectConfigSlice,
  (configState) => configState?.currencyConversionRates?.base,
);

export const getConversionRates = createSelector(
  selectConfigSlice,
  (configState) => configState?.currencyConversionRates?.rates || emptyObj,
);

// buffer in percentage
export const getWalletConversionBuffer = createSelector(
  selectConfigSlice,
  (configState) => configState?.cBuf || 0,
);

export const getRecipientInvitationCSVTemplate = createSelector(
  selectConfigSlice,
  (configState) => configState?.customizedEmailInviteSampleCsvUrl,
);

export const getPlatformFeeLabel = createSelector(
  selectConfigSlice,
  (configState) => configState?.platformFeeLabel || DEFAULT_PLATFORM_FEE_LABEL,
);

export const getPlatformFeeForOrganizerOrder = createSelector(
  selectConfigSlice,
  (configState) => configState?.platformFeeOrganizerOrder || DEFAULT_PLATFORM_FEE,
);

export const getPompFeeInUsd = createSelector(
  selectConfigSlice,
  (configState) => configState?.priceTemplates?.[0]?.pompFee || DEFAULT_POMP_FEE,
);

export const getPompLabel = createSelector(
  selectConfigSlice,
  (configState) => configState?.pompLabel,
);
export const getPompDescription = createSelector(
  selectConfigSlice,
  (configState) => configState?.pompDescription,
);

export const selectCountries = createSelector(
  selectConfigSlice,
  (configState) => configState?.countryMapping,
);

export const selectAllCountries = createSelector(
  selectConfigSlice,
  (configState) => configState?.countries || emptyArr,
);

export const selectCountryById = createSelector(
  selectAllCountries,
  (_, id) => id,
  (countries, countryId) => countries.find(({ id }) => id === countryId),
);

export const selectUsaCountryId = createSelector(
  selectConfigSlice,
  (configState) => configState?.usaCountryId,
);

export const selectActiveProgramDetail = createSelector(
  selectConfigSlice,
  (configState) => configState?.activeProgramDetail,
);

export const selectIsConfigLoading = createSelector(
  selectConfigSlice,
  (configState) => configState?.isFetching,
);

export const selectCurrencies = createSelector(
  selectConfigSlice,
  (configState) => configState?.currencies || emptyArr,
);

export const selectDefaultCurrencyCode = createSelector(
  selectConfigSlice,
  (configState) => configState?.currencyConversionRates?.base,
);

export const selectConversionRates = createSelector(
  selectConfigSlice,
  (configState) => configState?.currencyConversionRates?.rates || emptyObj,
);

export const selectPointsPerShopCurrency = createSelector(
  selectConversionRates,
  (_, shopCurrencyCode) => shopCurrencyCode,
  (rates, shopCurrencyCode) => rates?.[shopCurrencyCode?.toLowerCase()],
);

export const selectPointsPerUSD = createSelector(selectConversionRates, (rates) => rates?.stc);

export const selectRecipientInvitationCSVTemplate = createSelector(
  selectConfigSlice,
  (configState) => configState?.customizedEmailInviteSampleCsvUrl,
);

export const selectPointsPerGivenCurrency = createSelector(
  selectConversionRates,
  (_, givenCurrencyCode) => givenCurrencyCode,
  (rates, givenCurrencyCode) => rates?.[givenCurrencyCode?.toLowerCase()],
);

export const getStoreInitiationFee = createSelector(
  selectConfigSlice,
  (configState) => +configState.storeInitiationFeeInUsd,
);

export const getDefaultBuildYourOwnShopNumber = createSelector(
  selectConfigSlice,
  (configState) => configState?.defaultBuildYourOwnShopNumber || "",
);

export const getIsFetchingConfig = createSelector(
  selectConfigSlice,
  (configState) => configState?.isFetching,
);

export const getLockerShopFeUrl = createSelector(
  selectConfigSlice,
  (configState) => configState?.lockerShopFeUrl || "",
);

export const getPlatformFeeInfoMessage = createSelector(
  selectConfigSlice,
  (configState) => configState?.platformFeeInfoMessage || "",
);

export const getTaxRecoveryFeeInfoMessage = createSelector(
  selectConfigSlice,
  (configState) => configState?.taxRecoveryFeeInfoMessage || "",
);

export const getTaxRecoveryFeeRecipientInfoMessage = createSelector(
  selectConfigSlice,
  (configState) => configState?.taxRecoveryFeeRecipientInfoMessage || "",
);

export const getTaxRecoveryFeeLabel = createSelector(
  selectConfigSlice,
  (configState) => configState?.taxRecoveryFeeLabel || "",
);

export const getUsCountryId = createSelector(
  selectConfigSlice,
  (configState) => configState?.usCountryId || "",
);

export const getGlobalCountryZoneId = createSelector(
  selectConfigSlice,
  (configState) => configState?.globalCountryZoneId || "",
);

export const getWarehouseAddressId = createSelector(
  selectConfigSlice,
  (configState) => configState?.warehouseAddressId,
);

export const getWalletSavingPlans = createSelector(
  selectConfigSlice,
  (configState) => configState?.walletSavingPlans || [],
);

export const getDefaultShopNumber = createSelector(
  selectConfigSlice,
  (configState) => configState?.defaultShopNumber || [],
);

export const selectAllZones = createSelector(
  selectConfigSlice,
  (configState) => configState?.spreeZones || emptyArr,
);

export const getZonesMapping = createSelector(
  selectConfigSlice,
  (configState) => configState?.zonesMapping,
);

export const selectZoneByCountryId = createSelector(
  selectAllZones,
  (_, id) => id,
  (availableZones, countryId) =>
    availableZones?.find(({ uniqCountryId }) => uniqCountryId === countryId),
);

export const selectCountriesIncludingGlobal = createSelector(
  [getAllCountries, (state) => selectZoneByCountryId(state, null)],
  (countries: Array<any>, globalZone: any) => {
    return [...countries, { ...GLOBAL_COUNTRY, zoneId: globalZone.id }];
  },
);

export const getCountriesGroupedByZone = createSelector(
  selectCountriesIncludingGlobal,
  (countries) => groupBy(countries || emptyArr, "zoneId"),
);

export const selectCountriesIncludingGlobalUsingCountryZoneId = createSelector(
  [getAllCountries, getGlobalCountryZoneId],
  (countries, globalCountryZoneId) => [
    ...(countries || emptyArr),
    { ...GLOBAL_COUNTRY, zoneId: globalCountryZoneId },
  ],
);

export const getCountriesGroupedByZoneUsingCountryZoneId = createSelector(
  selectCountriesIncludingGlobalUsingCountryZoneId,
  (countries) => groupBy(countries || emptyArr, "zoneId"),
);

export const getUsZoneData = createSelector(
  getUsaCountryId,
  (state) => state,
  (usCountryId, configState) => {
    return selectZoneByCountryId(configState, usCountryId);
  },
);

export const selectInternationalDietaryRestrictions = ({ config }) =>
  config?.activeProgramDetail?.preferences?.dietaryOptions || emptyArr;

export const selectDefaultAskForPreferenceMessage = ({ config }) =>
  config?.defaultAskForPreferenceMessage;

export const selectUsBundleToNonUsFees = createSelector(
  selectConfigSlice,
  (configState) => configState?.usBundleToNonUsFeesInUsd || "",
);

export const getFeatureGuideVideos = createSelector(
  selectConfigSlice,
  (confgState) => confgState?.featureGuideVideos || emptyArr,
);

const getTaxableNumberCountries = createSelector(getAllCountries, (countries) =>
  countries?.filter(({ iso }) => TAXABLE_NUMBER_COUNTRY.includes(iso)),
);

export const makeTaxNumberRequiredForCountry = (countryId) =>
  createSelector(getTaxableNumberCountries, (countries) =>
    countries?.some((country) => country?.id === countryId),
  );

export const getCountriesGroupedByIso = createSelector(
  selectCountriesIncludingGlobal,
  (countries) => keyBy(countries || emptyArr, "iso"),
);

export const getCountriesGroupedByIsoUsingCountryZoneId = createSelector(
  selectCountriesIncludingGlobalUsingCountryZoneId,
  (countries) => keyBy(countries || emptyArr, "iso"),
);

export const selectRestrictionTaxons = createSelector(selectConfigSlice, ({ spreeTaxons }) =>
  Object.values(spreeTaxons).filter(({ name = "" }) => name !== RESTRICTION_TAXON_NAME),
);

export const getTaxonsMapping = createSelector(selectConfigSlice, ({ spreeTaxons }) =>
  keyBy(spreeTaxons, "id"),
);

export const selectCountryMappingByIso = createSelector(selectAllCountries, (countries) =>
  keyBy(countries, "iso"),
);

export const getStadiumTaxId = createSelector(
  selectConfigSlice,
  (configState) => configState?.stadiumTaxId || "",
);

export const getLogoDpiConfig = createSelector(selectConfigSlice, (configState) =>
  getLogoDpiConfigFromApi(configState),
);
